// src/components/Dashboard.js
import React, { useEffect, useState, useCallback } from 'react';
import { db } from '../firebase';
import { collection, getDocs, query, where, orderBy, limit, Timestamp, collectionGroup } from 'firebase/firestore';
import Modal from './Modal';
import { format } from 'date-fns';
import { 
  FaCalendarAlt, FaUsers, FaTimesCircle, FaSpinner, 
  FaMoneyBillWave, FaUserCheck, FaChartLine, FaRegClock
} from 'react-icons/fa';

/**
 * Dashboard Component
 * 
 * A professional dashboard displaying key metrics and event analytics
 * Features:
 * - Real-time statistics with detailed breakdowns
 * - Event overview with status tracking
 * - Financial metrics and registration analytics
 * - Performance optimized data fetching with error handling
 * - Responsive design with professional UI/UX
 * 
 * @component
 * @example
 * return (
 *   <Dashboard />
 * )
 */
const Dashboard = () => {
  /**
   * Core state management for dashboard data and UI
   * @typedef {Object} DashboardStats
   * @property {number} totalEvents - Total number of events
   * @property {number} activeEvents - Number of active events
   * @property {number} totalRegistrations - Total number of registrations
   * @property {Object} registrationBreakdown - Breakdown of registration statuses
   * @property {Object} paymentBreakdown - Breakdown of payment statuses
   * @property {number} totalIncome - Total income from successful payments
   * @property {Array} upcomingEvents - List of upcoming events
   */
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalEvents: 0,
    activeEvents: 0,
    totalRegistrations: 0,
    registrationBreakdown: {
      approved: 0,
      pending: 0,
      rejected: 0
    },
    paymentBreakdown: {
      success: 0,
      pending: 0,
      failed: 0
    },
    totalIncome: 0,
    upcomingEvents: []
  });

  /**
   * Modal state management for error handling and user feedback
   * @typedef {Object} ModalState
   * @property {boolean} isVisible - Whether the modal is visible
   * @property {string} title - Modal title
   * @property {string} message - Modal message
   * @property {string} type - Modal type (error/warning/info)
   * @property {Function} onConfirm - Confirmation callback
   */
  const [modal, setModal] = useState({
    isVisible: false,
    title: '',
    message: '',
    type: '',
    onConfirm: null
  });

  /**
   * Formats currency values consistently across the dashboard
   * @param {number} amount - Amount to format
   * @returns {string} Formatted amount with currency symbol
   */
  const formatCurrency = useCallback((amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }, []);

  /**
   * Formats date values consistently across the dashboard
   * @param {Date|Timestamp} date - Date to format
   * @returns {string} Formatted date string
   */
  const formatDate = useCallback((date) => {
    try {
      if (date instanceof Timestamp) {
        return format(date.toDate(), 'PPP');
      }
      return format(new Date(date), 'PPP');
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'Invalid Date';
    }
  }, []);

  /**
   * Calculates the final income after deducting payment gateway fees and GST
   * @param {number} amount - The total amount before deductions
   * @returns {Object} Object containing gross amount, fees, GST, and net amount
   */
  const calculateNetIncome = useCallback((amount) => {
    // Calculate payment gateway fees (2%)
    const gatewayFees = amount * 0.02;
    
    // Calculate GST on gateway fees (18%)
    const gstOnFees = gatewayFees * 0.18;
    
    // Calculate net amount
    const netAmount = amount - (gatewayFees + gstOnFees);
    
    return {
      grossAmount: amount,
      gatewayFees,
      gstOnFees,
      netAmount
    };
  }, []);

  /**
   * Fetches dashboard data with optimized queries and error handling
   * Implements batched queries for better performance
   */
  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Get current date for filtering
      const now = new Date();
      const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      // Batch queries for better performance
      const [eventsSnapshot, activeEventsSnapshot, allRegistrationsSnapshot] = await Promise.all([
        getDocs(collection(db, 'events')),
        getDocs(query(
          collection(db, 'events'),
          where('eventDate', '>=', Timestamp.fromDate(startOfDay)),
          orderBy('eventDate', 'asc'),
          limit(5)
        )),
        getDocs(collectionGroup(db, 'registrations'))
      ]);

      // Process events data
      const activeEvents = activeEventsSnapshot.docs.map(doc => {
        const eventData = doc.data();
        const registrationEndDate = eventData.registrationEndDate?.toDate();
        const registrationsOpen = registrationEndDate ? now < registrationEndDate : true;

        return {
          id: doc.id,
          ...eventData,
          registrationsOpen,
        };
      });

      // Process all registrations with error handling
      const allRegistrations = allRegistrationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Calculate registration status breakdown
      const registrationBreakdown = allRegistrations.reduce((acc, reg) => {
        const scrutinyStatus = reg.scrutiny || 'pending';
        acc[scrutinyStatus] = (acc[scrutinyStatus] || 0) + 1;
        return acc;
      }, { approved: 0, pending: 0, rejected: 0 });

      // Calculate payment status breakdown
      const paymentBreakdown = allRegistrations.reduce((acc, reg) => {
        const paymentStatus = reg.status || 'pending';
        acc[paymentStatus] = (acc[paymentStatus] || 0) + 1;
        return acc;
      }, { success: 0, pending: 0, failed: 0 });

      // Calculate total income and deductions from successful payments
      const incomeDetails = allRegistrations.reduce((total, reg) => {
        if (reg.status === 'success' && reg.paymentDetails?.totalAmount) {
          total.gross += reg.paymentDetails.totalAmount;
        }
        return total;
      }, { gross: 0 });

      const { grossAmount, gatewayFees, gstOnFees, netAmount } = calculateNetIncome(incomeDetails.gross);

      // Update dashboard statistics
      setStats({
        totalEvents: eventsSnapshot.size,
        activeEvents: activeEvents.length,
        totalRegistrations: allRegistrations.length,
        registrationBreakdown,
        paymentBreakdown,
        totalIncome: {
          gross: grossAmount,
          gatewayFees,
          gstOnFees,
          net: netAmount
        },
        upcomingEvents: activeEvents
      });

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Failed to load dashboard data. Please try again later.');
      setModal({
        isVisible: true,
        title: 'Error Loading Dashboard',
        message: 'There was a problem loading the dashboard data. Would you like to retry?',
        type: 'error',
        onConfirm: fetchDashboardData
      });
    } finally {
      setLoading(false);
    }
  }, [calculateNetIncome]);

  // Initial data fetch on component mount
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  /**
   * Renders a metric card with consistent styling
   * @param {Object} props - Component props
   * @param {string} props.title - Card title
   * @param {string|number} props.value - Metric value
   * @param {Function} props.icon - Icon component
   * @param {string} props.color - Card accent color
   */
  const MetricCard = ({ title, value, icon: Icon, color }) => (
    <div className={`p-6 rounded-lg shadow-sm bg-white border-l-4 ${color} hover:shadow-md transition-shadow duration-200`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="mt-2 text-3xl font-bold text-gray-900">{value}</p>
        </div>
        <div className={`p-3 rounded-full ${color.replace('border-', 'bg-').replace('-600', '-100')}`}>
          <Icon className={color.replace('border-', 'text-').replace('-100', '-600')} size={24} />
        </div>
      </div>
    </div>
  );

  /**
   * Renders a status breakdown card with detailed metrics
   * @param {Object} props - Component props
   * @param {string} props.title - Card title
   * @param {Object} props.stats - Status statistics
   * @param {Function} props.icon - Icon component
   * @param {string} props.baseColor - Base color for styling
   */
  const StatusBreakdownCard = ({ title, stats, icon: Icon, baseColor }) => (
    <div className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow duration-200">
      <div className="flex items-center gap-3 mb-4">
        <Icon className={`text-${baseColor}-600`} size={24} />
        <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
      </div>
      <div className="space-y-4">
        {Object.entries(stats).map(([status, count]) => (
          <div key={status} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <span className={`w-3 h-3 rounded-full bg-${
                status === 'success' || status === 'approved' ? 'green' :
                status === 'pending' ? 'yellow' :
                'red'
              }-500`} />
              <span className="text-sm font-medium capitalize">{status}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-gray-900">{count}</span>
              <span className="text-sm text-gray-500">
                ({((count / Object.values(stats).reduce((a, b) => a + b, 0)) * 100).toFixed(1)}%)
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  /**
   * Renders the loading state
   */
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center space-y-4">
          <FaSpinner className="animate-spin text-blue-500" size={40} />
          <p className="text-gray-600">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  /**
   * Renders the error state
   */
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <FaTimesCircle className="mx-auto text-red-500 mb-4" size={40} />
          <p className="text-gray-800 font-medium">{error}</p>
          <button
            onClick={fetchDashboardData}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-7xl mx-auto px-4 py-8">
      {/* Dashboard Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard Overview</h1>
        <button
          onClick={fetchDashboardData}
          className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          <FaRegClock size={16} />
          Refresh Data
        </button>
      </div>

      {/* Main Statistics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Events"
          value={stats.totalEvents}
          icon={FaCalendarAlt}
          color="border-blue-600"
        />
        <MetricCard
          title="Active Events"
          value={stats.activeEvents}
          icon={FaChartLine}
          color="border-green-600"
        />
        <MetricCard
          title="Total Registrations"
          value={stats.totalRegistrations}
          icon={FaUsers}
          color="border-purple-600"
        />
        <div className="col-span-1 lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-full bg-emerald-100">
                  <FaMoneyBillWave className="text-emerald-600" size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Revenue Breakdown</h3>
                  <p className="text-sm text-gray-500">After fees and taxes</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-emerald-600">{formatCurrency(stats.totalIncome.net)}</p>
                <p className="text-sm text-gray-500">Net Income</p>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-sm font-medium text-gray-600">Gross Revenue</span>
                <span className="text-base font-semibold text-gray-900">{formatCurrency(stats.totalIncome.gross)}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                <div>
                  <span className="text-sm font-medium text-red-600">Payment Gateway Fees</span>
                  <span className="text-xs text-red-500 ml-2">(2%)</span>
                </div>
                <span className="text-base font-semibold text-red-600">-{formatCurrency(stats.totalIncome.gatewayFees)}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                <div>
                  <span className="text-sm font-medium text-orange-600">GST on Fees</span>
                  <span className="text-xs text-orange-500 ml-2">(18%)</span>
                </div>
                <span className="text-base font-semibold text-orange-600">-{formatCurrency(stats.totalIncome.gstOnFees)}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-emerald-50 rounded-lg border-2 border-emerald-100">
                <div>
                  <span className="text-sm font-medium text-emerald-600">Net Income</span>
                  <span className="text-xs text-emerald-500 ml-2">(After deductions)</span>
                </div>
                <span className="text-lg font-bold text-emerald-600">{formatCurrency(stats.totalIncome.net)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Status Breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <StatusBreakdownCard
          title="Registration Status"
          stats={stats.registrationBreakdown}
          icon={FaUserCheck}
          baseColor="blue"
        />
        <StatusBreakdownCard
          title="Payment Status"
          stats={stats.paymentBreakdown}
          icon={FaMoneyBillWave}
          baseColor="green"
        />
      </div>

      {/* Upcoming Events Section */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <FaCalendarAlt className="text-blue-600" size={24} />
            <h3 className="text-lg font-semibold text-gray-800">Upcoming Events</h3>
          </div>
          <span className="text-sm text-gray-500">Next {stats.upcomingEvents.length} events</span>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {stats.upcomingEvents.length === 0 ? (
            <p className="text-gray-500 col-span-full text-center py-4">No upcoming events</p>
          ) : (
            stats.upcomingEvents.map(event => (
              <div key={event.id} className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex flex-col h-full">
                  <h4 className="font-medium text-gray-800 mb-2">{event.title}</h4>
                  <div className="space-y-2 flex-grow">
                    <p className="text-sm text-gray-500 flex items-center gap-2">
                      <FaCalendarAlt size={14} />
                      {formatDate(event.eventDate)}
                    </p>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                      ${event.registrationsOpen ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {event.registrationsOpen ? 'Registrations Open' : 'Registrations Closed'}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Modal Component */}
      <Modal
        isVisible={modal.isVisible}
        title={modal.title}
        message={modal.message}
        onClose={() => setModal({ ...modal, isVisible: false })}
        onConfirm={modal.onConfirm}
      />
    </div>
  );
};

export default Dashboard;